import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Search, 
  Filter,
  RefreshCw,
  Download,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  MoreHorizontal,
  Calendar,
  Users,
  ExternalLink
} from 'lucide-react';
import { toastService } from '../utils/toast';

interface PaymentTransaction {
  id: string;
  userId: string;
  userEmail: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'cancelled';
  paymentMethod: 'card' | 'apple_pay' | 'google_pay' | 'paypal';
  stripePaymentIntentId: string;
  coinPackageId: string;
  coinPackageName: string;
  coinsAmount: number;
  createdAt: number;
  completedAt?: number;
  failureReason?: string;
  refundAmount?: number;
  refundReason?: string;
  // Stripe díj számítások
  stripeFeeAmount?: number;
  stripeFeePercentage?: number;
  netAmount?: number; // összeg díj levonása után
  metadata?: {
    deviceType?: string;
    country?: string;
    ip?: string;
  };
}

interface PaymentStats {
  totalRevenue: number;
  totalTransactions: number;
  successfulTransactions: number;
  failedTransactions: number;
  refundedAmount: number;
  pendingTransactions: number;
  averageTransactionValue: number;
  // Stripe díj statisztikák
  totalStripeFees: number;
  netRevenue: number; // bruttó bevétel mínusz Stripe díjak
  stripeFeePercentage: number;
}

const PaymentsManagement: React.FC = () => {
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [selectedTransaction, setSelectedTransaction] = useState<PaymentTransaction | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  // Stripe díj számítás utility
  const calculateStripeFee = (amount: number, feePercentage: number = 2.9) => {
    const feeAmount = (amount * feePercentage) / 100;
    const fixedFee = 0.30; // Stripe fix díj USD-ben
    const totalFee = feeAmount + fixedFee;
    return {
      stripeFeeAmount: Math.round(totalFee * 100) / 100,
      netAmount: Math.round((amount - totalFee) * 100) / 100,
      stripeFeePercentage: feePercentage
    };
  };

  // Mock adatok - később Stripe API-ból és Firebase-ből jönnek majd
  useEffect(() => {
    const createMockTransaction = (baseData: Partial<PaymentTransaction>): PaymentTransaction => {
      const feeCalc = calculateStripeFee(baseData.amount || 0);
      return {
        ...baseData,
        ...feeCalc,
      } as PaymentTransaction;
    };

    const mockTransactions: PaymentTransaction[] = [
      createMockTransaction({
        id: 'pi_1234567890',
        userId: 'user1@example.com',
        userEmail: 'user1@example.com',
        amount: 9.99,
        currency: 'USD',
        status: 'completed',
        paymentMethod: 'card',
        stripePaymentIntentId: 'pi_1234567890',
        coinPackageId: 'popular',
        coinPackageName: 'Népszerű Csomag',
        coinsAmount: 500,
        createdAt: Date.now() - 3600000,
        completedAt: Date.now() - 3590000,
        metadata: {
          deviceType: 'mobile',
          country: 'HU',
          ip: '192.168.1.1'
        }
      }),
      createMockTransaction({
        id: 'pi_0987654321',
        userId: 'user2@example.com',
        userEmail: 'user2@example.com',
        amount: 39.99,
        currency: 'USD',
        status: 'completed',
        paymentMethod: 'apple_pay',
        stripePaymentIntentId: 'pi_0987654321',
        coinPackageId: 'premium',
        coinPackageName: 'Prémium Csomag',
        coinsAmount: 2500,
        createdAt: Date.now() - 7200000,
        completedAt: Date.now() - 7190000,
        metadata: {
          deviceType: 'mobile',
          country: 'US',
          ip: '10.0.0.1'
        }
      }),
      createMockTransaction({
        id: 'pi_1122334455',
        userId: 'user3@example.com',
        userEmail: 'user3@example.com',
        amount: 17.99,
        currency: 'USD',
        status: 'failed',
        paymentMethod: 'card',
        stripePaymentIntentId: 'pi_1122334455',
        coinPackageId: 'pro',
        coinPackageName: 'Pro Csomag',
        coinsAmount: 1000,
        createdAt: Date.now() - 10800000,
        failureReason: 'Insufficient funds',
        metadata: {
          deviceType: 'web',
          country: 'HU',
          ip: '192.168.1.3'
        }
      }),
      createMockTransaction({
        id: 'pi_5566778899',
        userId: 'user4@example.com',
        userEmail: 'user4@example.com',
        amount: 2.99,
        currency: 'USD',
        status: 'pending',
        paymentMethod: 'google_pay',
        stripePaymentIntentId: 'pi_5566778899',
        coinPackageId: 'starter',
        coinPackageName: 'Kezdő Csomag',
        coinsAmount: 100,
        createdAt: Date.now() - 1800000,
        metadata: {
          deviceType: 'android',
          country: 'HU',
          ip: '192.168.1.4'
        }
      }),
      createMockTransaction({
        id: 'pi_9988776655',
        userId: 'user5@example.com',
        userEmail: 'user5@example.com',
        amount: 9.99,
        currency: 'USD',
        status: 'refunded',
        paymentMethod: 'card',
        stripePaymentIntentId: 'pi_9988776655',
        coinPackageId: 'popular',
        coinPackageName: 'Népszerű Csomag',
        coinsAmount: 500,
        createdAt: Date.now() - 86400000,
        completedAt: Date.now() - 86390000,
        refundAmount: 9.99,
        refundReason: 'User request',
        metadata: {
          deviceType: 'web',
          country: 'HU',
          ip: '192.168.1.5'
        }
      })
    ];

    // Simulate API delay
    setTimeout(() => {
      setTransactions(mockTransactions);
      setLoading(false);
    }, 1000);
  }, []);

  // Statisztikák számítása
  const stats: PaymentStats = {
    totalRevenue: transactions.reduce((sum, t) => sum + (t.status === 'completed' ? t.amount : 0), 0),
    totalTransactions: transactions.length,
    successfulTransactions: transactions.filter(t => t.status === 'completed').length,
    failedTransactions: transactions.filter(t => t.status === 'failed').length,
    refundedAmount: transactions.reduce((sum, t) => sum + (t.refundAmount || 0), 0),
    pendingTransactions: transactions.filter(t => t.status === 'pending').length,
    averageTransactionValue: transactions.length > 0 
      ? transactions.reduce((sum, t) => sum + t.amount, 0) / transactions.length 
      : 0,
    // Stripe díj számítások
    totalStripeFees: transactions.reduce((sum, t) => sum + (t.stripeFeeAmount || 0), 0),
    netRevenue: transactions.reduce((sum, t) => sum + (t.status === 'completed' ? (t.netAmount || t.amount) : 0), 0),
    stripeFeePercentage: 2.9 // ez Settings-ből jön majd
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'refunded':
        return <RefreshCw className="h-4 w-4 text-blue-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: PaymentTransaction['status']) => {
    const statusMap = {
      'completed': 'Befejezett',
      'pending': 'Függőben',
      'failed': 'Sikertelen',
      'refunded': 'Visszatérített',
      'cancelled': 'Törölve'
    };
    return statusMap[status] || status;
  };

  const getPaymentMethodText = (method: PaymentTransaction['paymentMethod']) => {
    const methodMap = {
      'card': 'Bankkártya',
      'apple_pay': 'Apple Pay',
      'google_pay': 'Google Pay',
      'paypal': 'PayPal'
    };
    return methodMap[method] || method;
  };

  const handleRefund = async (transactionId: string, amount: string, reason: string) => {
    const loadingToast = toastService.loading('Visszatérítés feldolgozása...');
    try {
      // Itt történne a Stripe refund API hívás
      console.log(`Refunding ${amount} for transaction ${transactionId}, reason: ${reason}`);
      
      // Mock success
      setTimeout(() => {
        toastService.dismiss(loadingToast);
        toastService.success('Visszatérítés sikeresen feldolgozva');
        setShowRefundModal(false);
        setRefundAmount('');
        setRefundReason('');
        
        // Update transaction status
        setTransactions(prev => prev.map(t => 
          t.id === transactionId 
            ? { ...t, status: 'refunded' as const, refundAmount: parseFloat(amount), refundReason: reason }
            : t
        ));
      }, 2000);
    } catch (error) {
      toastService.dismiss(loadingToast);
      toastService.error('Hiba a visszatérítés során');
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = 
      transaction.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.coinPackageName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
    
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const transactionDate = new Date(transaction.createdAt);
      const now = new Date();
      
      switch (dateFilter) {
        case 'today':
          matchesDate = transactionDate.toDateString() === now.toDateString();
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          matchesDate = transactionDate >= weekAgo;
          break;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          matchesDate = transactionDate >= monthAgo;
          break;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const handleExport = () => {
    const csvContent = [
      ['Tranzakció ID', 'Felhasználó', 'Összeg', 'Státusz', 'Fizetési Mód', 'Dátum', 'Aranytallér Csomag', 'Aranytallér Mennyiség', 'Hiba Oka', 'Visszatérítés', 'További Információk'],
      ...filteredTransactions.map(transaction => [
        transaction.id,
        transaction.userEmail,
        `${transaction.amount} ${transaction.currency}`,
        getStatusText(transaction.status),
        getPaymentMethodText(transaction.paymentMethod),
        new Date(transaction.createdAt).toLocaleDateString(),
        transaction.coinPackageName,
        transaction.coinsAmount.toLocaleString(),
        transaction.failureReason || '',
        transaction.refundAmount ? `${transaction.refundAmount} - ${transaction.refundReason}` : '',
        JSON.stringify(transaction.metadata)
      ])
    ];

    const csvString = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fizetési_tranzakciók_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const loadTransactions = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      // Transactions are already loaded in useEffect, just refresh the loading state
      setLoading(false);
      toastService.success('Tranzakciók frissítve');
    }, 1000);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Fizetések Kezelése</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => window.open('https://dashboard.stripe.com/payments', '_blank')}
            className="btn-secondary flex items-center"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Stripe Dashboard
          </button>
          <button
            onClick={handleExport}
            className="btn-secondary flex items-center"
          >
            <Download className="mr-2 h-4 w-4" />
            Export
          </button>
          <button
            onClick={loadTransactions}
            className="btn-primary flex items-center"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Frissítés
          </button>
        </div>
      </div>

      {/* Kifizetések Szekció */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <DollarSign className="mr-2 h-5 w-5 text-green-500" />
            Kifizetések
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-green-600 dark:text-green-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">Elérhető egyenleg</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                  ${(stats.totalRevenue * 0.97).toFixed(2)}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  Stripe díjak levonása után
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Függőben</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  ${(stats.totalRevenue * 0.03).toFixed(2)}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Következő kifizetés
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-gray-600 dark:text-gray-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Havi bevétel</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  ${stats.totalRevenue.toFixed(2)}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Ez a hónap
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => window.open('https://dashboard.stripe.com/balance/overview', '_blank')}
            className="btn-primary flex items-center"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Stripe Kifizetések Kezelése
          </button>
          
          <button
            onClick={() => toastService.info('Stripe fiók létrehozása és integrációja után elérhető')}
            className="btn-secondary flex items-center"
          >
            <DollarSign className="mr-2 h-4 w-4" />
            Azonnali Kifizetés Igénylése
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Keresés felhasználó, tranzakció ID vagy csomag alapján..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">Minden státusz</option>
                <option value="completed">Befejezett</option>
                <option value="pending">Függőben</option>
                <option value="failed">Sikertelen</option>
                <option value="refunded">Visszatérített</option>
              </select>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">Minden idő</option>
                <option value="today">Ma</option>
                <option value="week">Utolsó hét</option>
                <option value="month">Utolsó hónap</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Tranzakciók ({filteredTransactions.length})
          </h3>
        </div>
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tranzakció
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Felhasználó
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Összeg
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Státusz
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Fizetési Mód
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Dátum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {transaction.id}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {transaction.coinPackageName}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {transaction.userEmail}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        ${transaction.amount.toFixed(2)} {transaction.currency}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {transaction.coinsAmount.toLocaleString()} aranytallér
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(transaction.status)}
                        <span className="ml-2 text-sm text-gray-900 dark:text-white">
                          {getStatusText(transaction.status)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {getPaymentMethodText(transaction.paymentMethod)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(transaction.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setShowDetailsModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Részletek"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {transaction.status === 'completed' && (
                          <button
                            onClick={() => {
                              setSelectedTransaction(transaction);
                              setRefundAmount(transaction.amount.toString());
                              setShowRefundModal(true);
                            }}
                            className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300"
                            title="Visszatérítés"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {showDetailsModal && selectedTransaction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Tranzakció Részletei
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tranzakció ID</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTransaction.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Stripe Payment Intent</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTransaction.stripePaymentIntentId}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Felhasználó</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTransaction.userEmail}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Összeg</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      ${selectedTransaction.amount.toFixed(2)} {selectedTransaction.currency}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Aranytallér Csomag</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTransaction.coinPackageName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Aranytallér Mennyiség</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTransaction.coinsAmount.toLocaleString()}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Státusz</label>
                    <div className="mt-1 flex items-center">
                      {getStatusIcon(selectedTransaction.status)}
                      <span className="ml-2 text-sm text-gray-900 dark:text-white">
                        {getStatusText(selectedTransaction.status)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Fizetési Mód</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {getPaymentMethodText(selectedTransaction.paymentMethod)}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Létrehozva</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {new Date(selectedTransaction.createdAt).toLocaleString()}
                    </p>
                  </div>
                  {selectedTransaction.completedAt && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Befejezve</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">
                        {new Date(selectedTransaction.completedAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
                
                {selectedTransaction.metadata && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">További Információk</label>
                    <div className="mt-1 bg-gray-50 dark:bg-gray-700 p-3 rounded">
                      <p className="text-sm text-gray-900 dark:text-white">
                        Eszköz: {selectedTransaction.metadata.deviceType || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        Ország: {selectedTransaction.metadata.country || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        IP: {selectedTransaction.metadata.ip || 'N/A'}
                      </p>
                    </div>
                  </div>
                )}
                
                {selectedTransaction.failureReason && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Hiba Oka</label>
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {selectedTransaction.failureReason}
                    </p>
                  </div>
                )}
                
                {selectedTransaction.refundAmount && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Visszatérítés</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      ${selectedTransaction.refundAmount.toFixed(2)} - {selectedTransaction.refundReason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedTransaction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Visszatérítés
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="form-label">Tranzakció ID</label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{selectedTransaction.id}</p>
                </div>
                
                <div>
                  <label className="form-label">Visszatérítendő összeg</label>
                  <input
                    type="number"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="form-input"
                    max={selectedTransaction.amount}
                    step="0.01"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum: ${selectedTransaction.amount.toFixed(2)}
                  </p>
                </div>
                
                <div>
                  <label className="form-label">Visszatérítés oka</label>
                  <select
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    className="form-input"
                  >
                    <option value="">Válasszon okot</option>
                    <option value="User request">Felhasználói kérés</option>
                    <option value="Technical issue">Technikai probléma</option>
                    <option value="Duplicate payment">Dupla fizetés</option>
                    <option value="Fraudulent">Csalás gyanú</option>
                    <option value="Other">Egyéb</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowRefundModal(false)}
                  className="btn-secondary"
                >
                  Mégse
                </button>
                <button
                  onClick={() => handleRefund(selectedTransaction.id, refundAmount, refundReason)}
                  className="btn-primary"
                  disabled={!refundAmount || !refundReason}
                >
                  Visszatérítés
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsManagement; 